/** @format */
import { RefObject, useEffect, useRef, useState } from 'react';

/**
 * @deprecated use @feather/hooks/use-buffered-mesurements
 */
// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type BufferedMeasurement = {
    width: number;
    height: number;
};

/**
 * @deprecated use @feather/hooks/use-buffered-mesurements
 */
// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type BufferedMeasurements<T extends object> = {
    [key in keyof T]: BufferedMeasurement;
};

type BufferedMeasurementRefs<T extends object> = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
    [key in keyof T]: RefObject<any>;
};

/**
 * @deprecated use @feather/hooks/use-buffered-mesurements
 */
// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum BufferedMeasurementsStatus {
    CLEARING = 'clearing',
    MEASURING = 'measuring',
    FINISHED = 'finished',
}

/**
 * @deprecated use @feather/hooks/use-buffered-mesurements
 */
// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type UseBufferedMeasurementsProps<T extends object> = {
    refs: BufferedMeasurementRefs<T>;
};

/**
 * @deprecated use @feather/hooks/use-buffered-mesurements
 */
export const useBufferedMeasurements = <T extends object>({
    refs,
}: UseBufferedMeasurementsProps<T>) => {
    const refKeys = Object.keys(refs);
    const defaultMeasurements = refKeys.reduce(
        (measurements, refKey) => ({
            ...measurements,
            [refKey]: { width: 0, height: 0 },
        }),
        {} as BufferedMeasurements<T>
    );

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [status, setStatus] = useState<BufferedMeasurementsStatus>(
        BufferedMeasurementsStatus.CLEARING
    );

    const [measurements, setMeasurements] = useState(defaultMeasurements);

    const setStatusNextRender = (newStatus: BufferedMeasurementsStatus) => {
        timerRef.current = setTimeout(() => setStatus(newStatus), 0);
    };

    const clearScheduledStatusUpdates = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    };

    const remeasure = () => {
        if (status === BufferedMeasurementsStatus.FINISHED) {
            setStatus(BufferedMeasurementsStatus.CLEARING);
        }
    };

    const clearMeasurements = () => {
        setMeasurements(defaultMeasurements);
        setStatusNextRender(BufferedMeasurementsStatus.MEASURING);
    };

    const getMeasurements = () => {
        const newMeasurements = {} as BufferedMeasurements<T>;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
        for (const [key, value] of Object.entries<RefObject<any>>(refs)) {
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing, @typescript-eslint/no-unsafe-assignment -- Legacy code: do not use as an example.
            const { clientWidth, clientHeight } = value?.current || {};
            // @ts-ignore - Element implicitly has an any type - Touch and Change
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Legacy code: do not use as an example.
            newMeasurements[key] = { width: clientWidth, height: clientHeight };
        }

        setMeasurements(newMeasurements);
        setStatusNextRender(BufferedMeasurementsStatus.FINISHED);
    };

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- Legacy code: do not use as an example.
        switch (status) {
            case BufferedMeasurementsStatus.CLEARING:
                clearMeasurements();
                break;
            case BufferedMeasurementsStatus.MEASURING:
                getMeasurements();
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [status]);

    useEffect(() => {
        return () => clearScheduledStatusUpdates();
    }, []);

    return { status, measurements, remeasure };
};
